import { useState } from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle, AlertTriangle, Info, Plus } from "lucide-react";
import { toast } from "sonner";
import { useApiClient } from "@/lib/api-client";
import { useAuthContext } from "@/components/auth/ClerkAuthProvider";
import type { Tenant } from "@/types/firestore";

interface CreditAlertBannerProps {
  tenant: Tenant;
}

export function CreditAlertBanner({ tenant }: CreditAlertBannerProps) {
  const [isLoading, setIsLoading] = useState(false);
  const api = useApiClient();
  const { isSuperAdmin } = useAuthContext();

  // Super admins have unlimited credits - no alerts needed
  if (isSuperAdmin) return null;

  const monthlyCredits = tenant.generation_credits_monthly ?? 0;
  const addonCredits = tenant.generation_credits_addon ?? 0;
  const rolloverCredits = tenant.generation_credits_rollover ?? 0;
  const totalCredits = monthlyCredits + addonCredits + rolloverCredits;
  const subscriptionTier = tenant.subscription_tier;

  const handleBuyCredits = async () => {
    setIsLoading(true);
    try {
      const data = await api.post<{ sessionId: string; url: string }>(
        "/stripe/buy-credits",
        {}
      );

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (error) {
      console.error("Error creating checkout session:", error);
      toast.error("Failed to start checkout", {
        description: "Please try again or contact support if the issue persists.",
      });
      setIsLoading(false);
    }
  };

  // Zero credits - Red alert
  if (totalCredits === 0) {
    return (
      <div className="mb-6 rounded-lg border border-destructive/50 bg-destructive/10 p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-destructive">Out of Generation Credits</h3>
            <p className="mt-1 text-sm text-destructive/90">
              New leads will not receive AI-generated design previews until you add more credits.
            </p>
          </div>
          <Button
            onClick={handleBuyCredits}
            disabled={isLoading}
            size="sm"
            variant="destructive"
            className="flex-shrink-0 gap-2"
          >
            <Plus className="h-4 w-4" />
            {isLoading ? "Loading..." : "Buy Credits"}
          </Button>
        </div>
      </div>
    );
  }

  // Low credits (1-2 remaining) - Amber warning
  if (totalCredits <= 2 && totalCredits > 0) {
    return (
      <div className="mb-6 rounded-lg border border-amber-500/50 bg-amber-500/10 p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-amber-900 dark:text-amber-100">
              Low on Credits
            </h3>
            <p className="mt-1 text-sm text-amber-800 dark:text-amber-200">
              You have {totalCredits} {totalCredits === 1 ? "credit" : "credits"} remaining.
              Stock up now to keep capturing leads without interruption.
            </p>
          </div>
          <Button
            onClick={handleBuyCredits}
            disabled={isLoading}
            size="sm"
            variant="outline"
            className="flex-shrink-0 gap-2 border-amber-600 text-amber-900 hover:bg-amber-100 dark:text-amber-100 dark:hover:bg-amber-900/20"
          >
            <Plus className="h-4 w-4" />
            {isLoading ? "Loading..." : "Buy More"}
          </Button>
        </div>
      </div>
    );
  }

  // Free tier - Info banner
  if (subscriptionTier === "free") {
    return (
      <div className="mb-6 rounded-lg border border-blue-500/50 bg-blue-500/10 p-4">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-blue-900 dark:text-blue-100">
              Free Plan
            </h3>
            <p className="mt-1 text-sm text-blue-800 dark:text-blue-200">
              Upgrade to Pro for unlimited monthly credits and advanced features.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="flex-shrink-0 border-blue-600 text-blue-900 hover:bg-blue-100 dark:text-blue-100 dark:hover:bg-blue-900/20"
            asChild
          >
            <a href="/settings">Upgrade</a>
          </Button>
        </div>
      </div>
    );
  }

  // No alert needed
  return null;
}
